import { Api } from '@automapa/svelte-automapa-template';
import {
    JobCountSchema,
    type JobCountType,
    JobResponseSchema,
    type JobResponseType,
} from '../Jobs/types';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { z } from 'zod';
import { QueriesTypeSchema } from '../telematics/types';
import type { QueriesType } from '../telematics/types';
import { JobServiceAttributesSchema } from '../Jobs/types';
import { Assignment } from './Assignment';
import { getFilterAssignmentId } from './getFilterAssignmentId';
import { get } from 'svelte/store';
import { planning } from '../../../stores/store';
import { features } from '../../shared/Features/features';

export const AssignmentJobRequestSchema = z.object({
    job_id: z.number(),
    assignment_id: z.number(),
    tactical_plan_rule_id: z.number().nullable(),
});
export type AssignmentJobRequestType = z.infer<typeof AssignmentJobRequestSchema>;

export const AssignmentJobResponseSchema = AssignmentJobRequestSchema.merge(
    z.object({
        job: JobResponseSchema.nullable()
            // .catch(null)
            .describe(
                'have to be optional due to create will return only AssignmentJobResponse without JobResponse'
            ).catch(null),
        id: z.number(),
        updated: z.string().nullable().catch(null),
        created: z.string().nullable().catch(null),
        revision: z.string().catch(''),
        suggested_assignment_id: z.number().catch(-1), // -1
        details: z
            .object({
                optional: z.boolean().catch(false),
                overdue: z.boolean().catch(false),
                in_realisation: z.boolean().catch(false),
                service_attributes: JobServiceAttributesSchema.optional(),
            })
            .catch({ optional: false, overdue: false, in_realisation: false }),
    })
);
export type AssignmentJobResponseType = z.infer<typeof AssignmentJobResponseSchema>;

export const AssignmentJobSuggestSchema = z.object({
    job_id: z.number(),
    assignment_id: z.number(),
    suggested_assignment_id: z.number().catch(-2), // -2
    id: z.number(),
});
export type AssignmentJobSuggestType = z.infer<typeof AssignmentJobSuggestSchema>;

export const AssignmentJobAssignmentRequestSchema = z.object({
    assignment_id: z.number(),
    unassigned: z.boolean(),
    suggestions: z.boolean(),
    assignments: z.boolean().optional(),
    only_in_realisation: z.boolean().optional(),
    day: z.string().optional(),
    query: QueriesTypeSchema.optional(),
});
export type AssignmentJobAssignmentRequestType = z.infer<
    typeof AssignmentJobAssignmentRequestSchema
>;

export const AssignmentJobCounterForAssignmentSchema = z.object({
    filtered: z.number(),
    total: z.number(),
});
export const AssignmentJobCountersForAssignmentSchema = z.object({
    all: AssignmentJobCounterForAssignmentSchema,
    unassigned: AssignmentJobCounterForAssignmentSchema.optional(),
    suggestions: AssignmentJobCounterForAssignmentSchema.optional(),
    assignments: AssignmentJobCounterForAssignmentSchema.optional(),
});
export type AssignmentJobCountersForAssignmentType = z.infer<
    typeof AssignmentJobCountersForAssignmentSchema
>;

/*
assignment_job będzie miał atrybut details
Dla zleceń nieprzypisanych będzie on mógł mieć takie atrybuty:
{"newly_added" : false, "optional" : true, "overdue" : false}
Gdzie każdy klucz to jakaś flaga, którą (jeśli true) należy zwizualizowac na liście. Jeśli możesz przygotować na to frontend jeszcze bez backendu to byłoby super.
aha, i tam będzie jeszcze "suggested_assignment_id"
w tym details
 */

export class AssignmentJobApi {
    public static async getOne(api: Api, id: number): Promise<AssignmentJobResponseType> {
        let f = new ApiFilterBuilder(1);
        f.addRelation('job*');
        return api.getData<undefined, AssignmentJobResponseType>(
            'get',
            '/assignment_job/' + id + f.getUri(),
            undefined,
            z.undefined(),
            AssignmentJobResponseSchema
        );
    }

    public static async getByAssignmentId(
        api: Api,
        assignmentId: number
    ): Promise<AssignmentJobResponseType[]> {
        return api.getData<undefined, AssignmentJobResponseType[]>(
            'get',
            `/assignment_jobs/` +
                new ApiFilterBuilder(null)
                    .filterByNumber('assignment_id', assignmentId)
                    .addRelation('job*')
                    .getUri(),
            undefined,
            z.undefined(),
            z.array(AssignmentJobResponseSchema)
        );
    }

    public static async getWithQueries<T = AssignmentJobResponseType[]>(
        api: Api,
        queries: QueriesType,
        filter: ApiFilterBuilder,
        returnSchema: any | null = null
    ): Promise<T> {
        if (returnSchema === null) {
            returnSchema = z.array(AssignmentJobResponseSchema);
        }
        return api.getData<QueriesType, T>(
            'post',
            '/assignment_jobs/query' + filter.getUri(),
            queries,
            z.any(),
            returnSchema
            // filter.getProjectionFields().length == 0
            //     ? z.array(AssignmentJobResponseSchema)
            //     : z.array(z.any())
        );
    }

    public static async getForAssignmentWithQueries<T = AssignmentJobResponseType[]>(
        api: Api,
        queries: any,
        filter: ApiFilterBuilder,
        returnSchema: any | null = null
    ): Promise<T> {
        if (returnSchema === null) {
            returnSchema = z.array(AssignmentJobResponseSchema);
        }
        return api.getData<any, T>(
            'post',
            '/assignment_jobs/query_in_assignment' + filter.getUri(),
            queries,
            AssignmentJobAssignmentRequestSchema,
            returnSchema
            // filter.getProjectionFields().length == 0
            //     ? z.array(AssignmentJobResponseSchema)
            //     : z.array(z.any())
        );
    }

    public static async getCountWithQueries(
        api: Api,
        queries: QueriesType,
        filter: ApiFilterBuilder
    ): Promise<JobCountType> {
        return api.getData<QueriesType, JobCountType>(
            'post',
            '/assignment_jobs/count/query' + filter.getUri(),
            queries,
            z.any(),
            JobCountSchema
        );
    }

    public static async getCountWithQueriesForAssignment(
        api: Api,
        queries: AssignmentJobAssignmentRequestType
    ): Promise<AssignmentJobCountersForAssignmentType> {
        let f = new ApiFilterBuilder(null);
        f.setRelations(['job*']);
        f.setProjectionFields(['assignment_id', 'suggested_assignment_id', 'filter', 'count()']);
        f.setGroupByFields(['assignment_id', 'suggested_assignment_id', 'filter']);
        f.sortBy(null, null);
        return api.getData<
            AssignmentJobAssignmentRequestType,
            AssignmentJobCountersForAssignmentType
        >(
            'post',
            '/assignment_jobs/count_in_assignment' + f.getUri(),
            queries,
            AssignmentJobAssignmentRequestSchema,
            AssignmentJobCountersForAssignmentSchema
        );
    }

    public static async getCountWithQueriesAdHocsOne(
        api: Api,
        assignment_id: number,
        target_date: string
    ) {
        let f = new ApiFilterBuilder(1);
        f.setRelations(['job*']);
        f.setProjectionFields(['id']);
        let query = {};
        if (assignment_id === 0) {
            query = {
                'job.target_date': target_date,
                'job.ad_hoc': true,
                assignment_id: 0,
            };
        } else {
            query = {
                'job.ad_hoc': true,
                $or: [
                    {
                        assignment_id: {
                            $in: [assignment_id],
                        },
                    },
                    {
                        suggested_assignment_id: {
                            $in: [assignment_id],
                        },
                    },
                ],
            };
        }
        return api.getData<any, JobCountType>(
            'post',
            '/assignment_jobs/count/query' + f.getUri(),
            query,
            z.any(),
            JobCountSchema
        );
    }

    public static async getCountWithQueriesAdHocsMany(
        api: Api,
        assignment_ids: number[],
        target_date: string
    ) {
        let promises: Promise<JobCountType>[] = [];
        for (let assignment_id of assignment_ids) {
            // FIXME: optimize this to one query
            promises.push(this.getCountWithQueriesAdHocsOne(api, assignment_id, target_date));
        }
        let output: { assignment_id: number; count: number }[] = [];
        let values = await Promise.all(promises);
        for (let i = 0; i < values.length; i++) {
            output.push({ assignment_id: assignment_ids[i], count: values[i].count });
        }
        return output;
    }

    public static async getCountWithQueriesAdHocsMany2(
        api: Api,
        assignment_ids: number[],
        target_date: string,
        scenario_id: number
    ) {
        return api.getData<
            { assignment_ids: number[]; target_date: string; scenario_id: number },
            {
                group_by_assignments: {
                    assignment_id: number;
                    count: number;
                    _debug: any;
                    group_by_process_stage: {
                        to_assign: number;
                        to_approve: number;
                        to_send: number;
                        in_progress: number;
                    };
                }[];
            }
        >(
            'post',
            // '/assignment_jobs/get_ad_hoc_counts',
            '/assignment_jobs/get_adhoc_assignment_all_count',
            { assignment_ids, target_date, scenario_id },
            z.object({
                assignment_ids: z.array(z.number()),
                target_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
                scenario_id: z.number(),
            }),
            z.object({
                group_by_assignments: z.array(
                    z.object({
                        assignment_id: z.number(),
                        count: z.number(),
                        group_by_process_stage: z.object({
                            to_assign: z.number(),
                            to_approve: z.number(),
                            to_send: z.number(),
                            in_progress: z.number(),
                        }),
                    })
                ),
            })
        );
    }

    // UNUSED
    // public static async changeAssignmentId(api: Api, id: number, newId: number) {
    //     let ResponseSchema = AssignmentJobResponseSchema.omit({job: true});
    //     type ResponseType = z.infer<typeof ResponseSchema>;
    //     return api.patch<ResponseType>(
    //         '/assignment_job/' + id,
    //         'replace',
    //         '/assignment_id',
    //         newId,
    //         ResponseSchema
    //     );
    // }

    public static async remove(api: Api, id: number): Promise<void> {
        return api.delete('/assignment_job/' + id);
    }

    public static async create(
        api: Api,
        assignmentId: number,
        jobId: number
    ): Promise<AssignmentJobResponseType> {
        return api.getData<AssignmentJobRequestType, AssignmentJobResponseType>(
            'post',
            '/assignment_job',
            {
                assignment_id: assignmentId,
                job_id: jobId,
                tactical_plan_rule_id: null,
            },
            AssignmentJobRequestSchema,
            AssignmentJobResponseSchema
        );
    }

    public static async getSuggestedAssignmentIds(
        api: Api,
        assignmentIds: number[],
        xTargetDate: string
    ) {
        let f = new ApiFilterBuilder(1000);
        f.setProjectionFields(['suggested_assignment_id', 'assignment_id', 'job_id', 'id']);
        f.setRelations(['job*']);
        return api.getData<any, AssignmentJobSuggestType[]>(
            'post',
            '/assignment_job/query' + f.getUri(),
            {
                assignment_id: { $in: assignmentIds },
                suggested_assignment_id: { $gte: 0 },
                x_target_date: xTargetDate,
                // 'job.completed': null, // this should make faster query
            },
            z.any(),
            z.array(AssignmentJobSuggestSchema)
        );
    }

    public static async getSuggestedAssignmentIdsCount(
        api: Api,
        assignmentId: number,
        xTargetDate: string
    ) {
        let f = new ApiFilterBuilder(1);
        f.setRelations(['job*']);
        return api.getData<any, JobCountType>(
            'post',
            '/assignment_jobs/count/query' + f.getUri(),
            {
                assignment_id: 0,
                suggested_assignment_id: assignmentId,
                x_target_date: xTargetDate,
                // 'job.completed': null, // this should make faster query
            },
            z.any(),
            JobCountSchema
        );
    }

    // # curl 'http://lm.mdymowski.indigo/api/assignment_jobs/query?page%5Blimit%5D=20&page%5Boffset%5D=0&sort%5Bby%5D=job.address_attributes.city,job.address_attributes.street&sort%5Bdir%5D=asc,asc&relations%5B0%5D=job&fswitch=feature_1168_planning_page,feature_1168_planning_page_colors,feature_1212_manual_add_job,feature_1253_dashboard,feature_1343_backend_vehicle_assignment,feature_1355_auto_confirm,feature_1360_track_vehicles,feature_1360_track_vehicles_random,feature_assignment_job_context_menu,feature_1513_lifting_planning_page' -X POST -H 'User-Agent: Mozilla/5.0 (X11; Linux x86_64; rv:109.0) Gecko/20100101 Firefox/115.0' -H 'Accept: application/json, text/plain, */*' -H 'Accept-Language: pl' -H 'Accept-Encoding: gzip, deflate' -H 'Content-Type: application/json' -H 'Token: 572974bb0b2976e7813f22e3da14ca57' -H 'Origin: http://localhost:5173' -H 'Connection: keep-alive' -H 'Referer: http://localhost:5173/' --data-raw '{"job.target_date":"2024-05-15","job.ad_hoc":true,"$or":[{"assignment_id":0},{"assignment_id":{"$in":[112685,112684,112683,112682,112681,112680,112679,112678,112677,112676,112675,112674,112673]}},{"suggested_assignment_id":{"$in":[112685,112684,112683,112682,112681,112680,112679,112678,112677,112676,112675,112674,112673]}}]}'
    public static async getAdhocs(api: Api, query: QueriesType, filter: ApiFilterBuilder) {
        return api.getData<QueriesType, AssignmentJobResponseType[]>(
            'post',
            '/assignment_jobs/query' + filter.getUri(),
            query,
            z.any(),
            z.array(AssignmentJobResponseSchema)
        );
    }

    public static async changeAssignments(api: Api, query: QueriesType) {
        // console.warn('changeAssignments', query);
        // let f = new ApiFilterBuilder(1);
        // f.setRelations(['job*']);
        return api.getData<QueriesType, any>(
            'post',
            '/assignment_job/assign',
            query,
            z.any(),
            z.object({ assigned: z.number().catch(-1) })
        );
    }

    public static async setVehicle(api: Api, query: QueriesType) {
        return api.getData<QueriesType, any>(
            'post',
            '/assignment_job/set_vehicle',
            query,
            z.any(),
            z.object({ assigned: z.number().catch(0) })
        );
    }

    public static async getByJobId(api: Api, jobId: number): Promise<AssignmentJobResponseType> {
        // let jobs = await api.getData<any, JobResponseType[]>(
        //     'post',
        //     '/jobs/query',
        //     { id: jobId },
        //     z.object({ id: z.number() }),
        //     z.array(JobResponseSchema)
        // );
        // if (jobs && jobs.length > 0) {
        //     return this.getOne(api, jobs[0].id);
        // }
        // throw new Error('Job not found');
        let f = new ApiFilterBuilder(1);
        f.setRelations(['job*']);
        let ajs = await api.getData<any, AssignmentJobResponseType[]>(
            'post',
            '/assignment_jobs/query' + f.getUri(),
            { job_id: jobId },
            z.any(),
            z.array(AssignmentJobResponseSchema)
        );
        if (ajs && ajs.length > 0) {
            return ajs[0];
        }
        throw new Error('AssignmentJob not found');
    }

    public static async getIds(
        api: Api,
        assignment: Assignment,
        withSuggestions: boolean = false,
        withUnassigned: boolean = false
    ) {
        let f = new ApiFilterBuilder(null);
        f.setRelations(['job*']);
        f.setProjectionFields(['job_id']);
        return api.getData<AssignmentJobAssignmentRequestType, any>(
            'post',
            '/assignment_jobs/query_in_assignment' + f.getUri(),
            {
                assignment_id: assignment.getId(),
                unassigned: withUnassigned,
                suggestions: withSuggestions,
                day: get(planning).selectedDay,
            },
            AssignmentJobAssignmentRequestSchema,
            z.array(z.object({ job_id: z.number() }))
        );
    }

    public static async getAllIds(api: Api, assignment: Assignment) {
        return AssignmentJobApi.getIds(api, assignment, true, true);
        // return assignment.isDefault()
        //     ? this.getWithQueries(
        //           api,
        //           getFilterAssignmentId(
        //               true,
        //               true,
        //               assignment.isDefault(),
        //               assignment.getId(),
        //               get(planning).selectedDay
        //           ),
        //           f
        //       )
        //     : null;
    }
}
